/**
 * CSV のひな型（見出しだけの空のCSV）を組み立てる。
 *
 * **項目の決まりは画面に書きません。** サーバの `GET /admin/roster/columns` が返すものから
 * 作ります（定義元は `server/src/roster/columns.ts` の1か所）。項目は要件定義で確定するので、
 * 決まったらサーバ側を直すだけで、この画面の説明もひな型も変わります。
 */
import type { RosterColumn } from '../api/types.js';
import { displayColumnLabel } from './errors.js';

/** ひな型のファイル名。 */
export const ROSTER_TEMPLATE_FILENAME = '名簿のひな型.csv';

/**
 * ファイルの先頭に付ける見えない印（BOM）。
 * これが無いと、Excel で開いたときに日本語が化けます。
 */
const BOM = '\ufeff';

/**
 * 見出しだけの CSV を作る。
 * 中身は入れません（お手元の台帳から貼り付けていただくための空の用紙です）。
 */
export function buildRosterTemplateCsv(columns: readonly RosterColumn[]): string {
  // 氏・名に分けて書く列は、表示名・ふりがなの列の代わりなので、ひな型には入れない（issue #521）
  return `${columns
    .filter((column) => column.inTemplate !== false)
    .map((column) => escapeCsvValue(displayColumnLabel(column.label)))
    .join(',')}\n`;
}

/** カンマ・引用符・改行を含む値は、CSV の決まりどおり二重引用符で囲む。 */
function escapeCsvValue(value: string): string {
  return /[",\n\r]/u.test(value) ? `"${value.replace(/"/gu, '""')}"` : value;
}

/**
 * ダウンロードできる住所を作る。
 *
 * 先頭に見えない印（BOM）を付けるのは、Excel で開いたときに日本語が化けないようにするため。
 * ブラウザによっては下の作り方が使えないので、そのときは文字をそのまま載せた住所に切り替える
 * （どちらでも、押せば同じ中身が落ちてくる）。
 */
export function csvDownloadUrl(text: string): string {
  const body = BOM + text;
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    try {
      return URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
    } catch {
      // 使えないときは、下の書き方にそのまま落とす
    }
  }
  return `data:text/csv;charset=utf-8,${encodeURIComponent(body)}`;
}

/** 作った住所を片付ける（作れていなければ何もしない）。 */
export function revokeDownloadUrl(url: string): void {
  if (!url.startsWith('blob:')) return;
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}
